/* eslint-disable @next/next/no-img-element */
import Link from 'next/link'
import { BsStars } from 'react-icons/bs'
import { MdOutlineQueryStats, MdOutlineStorefront } from 'react-icons/md'
import { FaTwitter, FaDiscord } from 'react-icons/fa'
import Meta from 'components/Meta'
import Layout from 'components/Layout'
import RecentSales from 'components/RecentSales'
import TourDesktop from 'components/home/TourDesktop'
import type { GetServerSideProps } from 'next'
import HeroLink from 'components/home/HeroLink'

const cardLinks = [
  {
    name: 'Mint your NFD',
    href: '/mint',
    description: `Get started by minting an NFD. Search for the name you want and see if it's available, then connect your wallet to purchase.`,
    icon: BsStars,
    linkText: 'Mint search'
  },
  {
    name: 'Marketplace',
    href: '/browse',
    description:
      'Browse the marketplace to find NFDs for sale or currently in auction. You can also search for existing NFDs by name or filter by category.',
    icon: MdOutlineStorefront,
    linkText: 'Marketplace search'
  },
  {
    name: 'Analytics',
    href: '/analytics',
    description:
      'Track the latest market activity and see the highest all-time sales. More detailed analytics coming soon!',
    icon: MdOutlineQueryStats,
    linkText: 'View Analytics'
  }
]

export const socialLinks = [
  {
    name: 'Twitter',
    href: 'https://twitter.com/nfdomains',
    icon: FaTwitter
  },
  {
    name: 'Discord',
    href: 'https://discord.gg/7XcuMTfeZP',
    icon: FaDiscord
  }
]

type HomeProps = {
  pathname: string
}

export default function Home({ pathname }: HomeProps) {
  return (
    <>
      <Meta />
      <Layout pathname={pathname}>
        <div className="bg-white">
          <header className="relative pt-16 sm:pt-24 pb-36 bg-brand-500">
            <div className="absolute inset-0">
              <img
                className="w-full h-full object-cover object-bottom-right"
                src="/img/nfd-brand-bg.jpg"
                alt=""
              />
            </div>

            <div className="relative max-w-md mx-auto px-4 pb-16 sm:pb-24 sm:max-w-3xl sm:px-6 xl:max-w-7xl xl:px-8">
              <HeroLink
                href="https://docs.nf.domains/docs/NFD%20Overview/What%20is%20an%20NFD"
                badge="Overview"
                linkText="What is an NFD?"
                className="mb-8 sm:mb-10"
              />
              <h1 className="text-5xl font-extrabold tracking-tight text-white sm:text-7xl">
                Welcome
              </h1>
              <p className="mt-6 max-w-3xl text-xl text-brand-100 sm:mt-8">
                NFDomains is the Algorand name service and marketplace for{' '}
                <strong className="text-white sm:whitespace-nowrap">Non-Fungible Domains</strong>{' '}
                &mdash; unique, readable identities for your wallet.
              </p>
              <p className="text-brand-100 text-xs font-semibold tracking-wide mt-6 uppercase">
                Built on
                <svg viewBox="0 0 1113.6 301.6" className="w-24 h-auto inline ml-2 mb-2 opacity-80">
                  <g fill="white">
                    <path d="M293.1,59.5V240h-36.8V59.5H293.1z" />
                    <path d="M443.3,118.3v122.1c0,37.9-29.5,61.2-65.6,61.2c-34.8,0-60.8-21.6-63.3-52.1l36.7,0.2c1.5,13,12.9,18.6,27.9,18.6 c14.2,0,27.4-7.6,27.4-27.4v-15.6c-9.4,6.1-21.4,9.7-34.8,9.7c-33.6,0-60.5-27.2-60.5-58.5c0-36.4,27-60.8,60.5-60.8 c13.5,0,25.4,3.6,34.8,9.7v-6.6L443.3,118.3z M406.5,190.2v-29.7c-7.5-10.9-17.9-14.5-28.2-14.5c-18.1,0-30.7,12.1-30.7,30.5 c0,13.5,12.6,28.3,30.7,28.3C388.6,204.7,399,201,406.5,190.2z" />
                    <path d="M590.1,177.7c0,37.6-27.8,64.4-65.1,64.4c-37.6,0-65.4-26.8-65.4-64.4c0-37.3,27.8-64.6,65.4-64.6 C562.3,113.1,590.1,140.4,590.1,177.7z M554.6,177.7c0-17.7-11.6-32-29.6-32c-18.2,0-29.8,14.3-29.8,32c0,18.2,11.6,31.8,29.8,31.8 C542.9,209.4,554.6,195.9,554.6,177.7z" />
                    <path d="M818.6,118.5v121.3l-36.1,0.2v-6.9c-9.4,6.1-21.4,9.9-34.8,9.9c-33.6,0-60.5-28.5-60.5-63.6c0-35.3,27-63.8,60.5-63.8 c13.5,0,25.4,3.8,34.8,9.9v-6.9L818.6,118.5z M782.5,198v-37.2c-7.6-10.8-18.2-13.8-28.8-13.8c-18.5,0-31.3,14.8-31.3,32.5 c0,17.5,12.8,32.3,31.3,32.3C764.3,211.7,774.9,208.8,782.5,198z" />
                    <path d="M967,174.1V240h-35.5v-61.6c0-19.8-9.9-30.3-27.5-30.3c-9.2,0-19.3,5.3-28,15.5V240h-36.3V118.7H876v9.2 c9.9-7.9,21.4-12.2,35.1-12.2C943.5,115.7,967,138.5,967,174.1z" />
                    <g id="lINT7W_1_">
                      <g>
                        <polygon points="239.5,239.9 202,239.9 177.6,149.2 125.1,239.9 83.2,239.9 164.2,99.5 151.2,50.7 41.9,240 0,240 138.5,0 175.2,0 191.3,59.6 229.2,59.6 203.3,104.6 			" />
                      </g>
                    </g>
                    <path d="M1113.6,59.5V240h-36.8v-6.9c-9.4,6.1-21.4,9.9-34.8,9.9c-33.6,0-60.5-28.5-60.5-63.8c0-35.1,27-63.6,60.5-63.6 c13.5,0,25.4,3.8,34.8,9.9V59.4L1113.6,59.5L1113.6,59.5z M1076.8,198.1v-37.5c-7.7-10.9-18.4-13.9-29-13.9 c-18.6,0-31.5,14.9-31.5,32.5c0,17.9,12.9,32.8,31.5,32.8C1058.4,212,1069.1,209,1076.8,198.1z" />
                    <path d="M680.6,149.3c-8,1-26.8,1.9-37.8,17.1v73.7h-36.3V118.9h36.3v9.1c8.3-7.1,20.8-11.3,37.8-9.6" />
                  </g>
                </svg>
              </p>
              <TourDesktop />
            </div>
          </header>

          <main>
            <div className="bg-gray-100">
              {/* Cards */}
              <section
                className="-mt-32 max-w-md mx-auto relative z-10 px-4 sm:max-w-3xl sm:px-6 xl:max-w-7xl xl:px-8"
                aria-labelledby="get-started-heading"
              >
                <h2 className="sr-only" id="get-started-heading">
                  Get started
                </h2>
                <div className="grid grid-cols-1 gap-y-20 xl:grid-cols-3 xl:gap-y-0 xl:gap-x-8">
                  {cardLinks.map((link) => (
                    <Link key={link.name} href={link.href}>
                      <a
                        key={link.name}
                        className="flex flex-col bg-white rounded-2xl shadow-xl outline-brand-500"
                      >
                        <div className="flex-1 relative pt-16 px-6 pb-8 md:px-8">
                          <div className="absolute top-0 p-5 inline-block bg-gradient-to-t from-brand-600 to-brand-700 rounded-xl shadow-lg transform -translate-y-1/2">
                            <link.icon className="h-6 w-6 text-white" aria-hidden="true" />
                          </div>
                          <h3 className="text-xl font-medium text-gray-900">{link.name}</h3>
                          <p className="mt-4 text-base text-gray-500">{link.description}</p>
                        </div>
                        <div className="p-6 bg-gray-50 rounded-bl-2xl rounded-br-2xl md:px-8">
                          <span className="text-base font-medium text-brand-500 hover:text-brand-400">
                            {link.linkText}
                            <span aria-hidden="true"> &rarr;</span>
                          </span>
                        </div>
                      </a>
                    </Link>
                  ))}
                </div>
              </section>

              {/* Recent Sales */}
              <section
                className="py-24 sm:max-w-3xl sm:mx-auto xl:max-w-7xl xl:py-32"
                aria-labelledby="recent-sales-heading"
              >
                <RecentSales />
              </section>
            </div>

            {/* CTA Section */}
            <section className="relative bg-white" aria-labelledby="join-discord">
              <div
                className="hidden absolute inset-x-0 h-1/2 bg-gray-100 xl:block"
                aria-hidden="true"
              />
              <div className="max-w-7xl mx-auto bg-brand-500 xl:bg-transparent xl:px-8">
                <div className="xl:grid xl:grid-cols-12">
                  <div className="relative z-10 xl:col-start-1 xl:row-start-1 xl:col-span-4 xl:py-16 xl:bg-transparent">
                    <div
                      className="absolute inset-x-0 h-1/2 bg-gray-100 xl:hidden"
                      aria-hidden="true"
                    />
                    <div className="max-w-md mx-auto px-4 sm:max-w-3xl sm:px-6 xl:max-w-none xl:p-0">
                      <div className="relative aspect-[10/6] sm:aspect-[2/1] xl:aspect-square">
                        <img
                          className="absolute inset-0 w-full h-full object-cover object-left-top rounded-3xl shadow-2xl"
                          src="/img/nfd-discord.jpg"
                          alt=""
                        />
                      </div>
                    </div>
                  </div>

                  <div className="relative bg-brand-500 xl:col-start-3 xl:row-start-1 xl:col-span-10 xl:rounded-3xl xl:grid xl:grid-cols-10 xl:items-center">
                    <div
                      className="hidden absolute inset-0 overflow-hidden rounded-3xl xl:block"
                      aria-hidden="true"
                    >
                      <svg
                        className="absolute bottom-full left-full transform translate-y-1/3 -translate-x-2/3 xl:bottom-auto xl:top-0 xl:translate-y-0"
                        width={404}
                        height={384}
                        fill="none"
                        viewBox="0 0 404 384"
                        aria-hidden="true"
                      >
                        <defs>
                          <pattern
                            id="64e643ad-2176-4f86-b3d7-f2c5da3b6a6d"
                            x={0}
                            y={0}
                            width={20}
                            height={20}
                            patternUnits="userSpaceOnUse"
                          >
                            <rect
                              x={0}
                              y={0}
                              width={4}
                              height={4}
                              className="text-brand-400"
                              fill="currentColor"
                            />
                          </pattern>
                        </defs>
                        <rect
                          width={404}
                          height={384}
                          fill="url(#64e643ad-2176-4f86-b3d7-f2c5da3b6a6d)"
                        />
                      </svg>
                      <svg
                        className="absolute top-full transform -translate-y-1/3 -translate-x-1/3 xl:-translate-y-1/2"
                        width={404}
                        height={384}
                        fill="none"
                        viewBox="0 0 404 384"
                        aria-hidden="true"
                      >
                        <defs>
                          <pattern
                            id="64e643ad-2176-4f86-b3d7-f2c5da3b6a6d"
                            x={0}
                            y={0}
                            width={20}
                            height={20}
                            patternUnits="userSpaceOnUse"
                          >
                            <rect
                              x={0}
                              y={0}
                              width={4}
                              height={4}
                              className="text-brand-400"
                              fill="currentColor"
                            />
                          </pattern>
                        </defs>
                        <rect
                          width={404}
                          height={384}
                          fill="url(#64e643ad-2176-4f86-b3d7-f2c5da3b6a6d)"
                        />
                      </svg>
                    </div>
                    <div className="relative max-w-md mx-auto py-12 px-4 space-y-6 sm:max-w-3xl sm:py-16 sm:px-6 xl:max-w-none xl:p-0 xl:col-start-4 xl:col-span-6">
                      <h2 className="text-3xl font-extrabold text-white" id="join-discord">
                        Join our Discord
                      </h2>
                      <p className="text-lg text-white">
                        Stay up to date with the latest news, feature announcements, and more in the
                        NFD Discord.
                      </p>
                      <p className="text-lg text-white">
                        We love to hear your feedback, suggestions and questions&hellip; ask us
                        anything.
                      </p>
                      <a
                        className="block w-full py-3 px-5 text-center bg-[#5865F2] border border-transparent rounded-md shadow-md text-base font-medium text-white hover:bg-[#4954CC] sm:inline-block sm:w-auto"
                        href="https://discord.gg/7XcuMTfeZP"
                        target="_blank"
                        rel="noreferrer"
                      >
                        <FaDiscord className="inline -ml-1 mr-3 h-6 w-6" aria-hidden="true" />
                        Let me in!
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </main>

          <footer className="mt-24 bg-gray-50" aria-labelledby="footer-heading">
            <h2 id="footer-heading" className="sr-only">
              Footer
            </h2>
            <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 md:flex md:items-center md:justify-between lg:px-8">
              <div className="flex justify-center space-x-6 md:order-2">
                {socialLinks.map((item) => (
                  <a
                    key={item.name}
                    href={item.href}
                    className="text-gray-400 hover:text-gray-500"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <span className="sr-only">{item.name}</span>
                    <item.icon className="h-6 w-6" aria-hidden="true" />
                  </a>
                ))}
              </div>
              <div className="mt-8 md:mt-0 md:order-1">
                <div className="text-center md:flex md:items-center md:justify-center">
                  <p className="text-gray-400">
                    &copy; {new Date().getFullYear()}{' '}
                    <a
                      href="https://txnlab.dev"
                      className="hover:text-brand-500"
                      target="_blank"
                      rel="noreferrer"
                    >
                      TxnLab, Inc.
                    </a>{' '}
                    All rights reserved.
                  </p>
                  <p className="mt-2 md:mt-0 md:ml-3 sm:border-l md:border-gray-200 md:pl-3 text-gray-400">
                    <a
                      href="https://nf.domains/privacy"
                      className="hover:text-brand-500"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Privacy Policy
                    </a>
                  </p>
                  <p className="mt-2 md:mt-0 md:ml-3 sm:border-l md:border-gray-200 md:pl-3 text-gray-400">
                    <a
                      href="https://nf.domains/terms"
                      className="hover:text-brand-500"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Terms of Service
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </footer>
        </div>
      </Layout>
    </>
  )
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  return {
    props: {
      pathname: context.resolvedUrl
    }
  }
}
